<script setup lang="ts">
// The population view's five readings: where the season stands this week, how
// that compares with the five seasons before it, and where it is heading. The
// verdicts are the operator's: falling is good, rising is bad, and the falling
// slope's colour says so without a second sentence.
import { computed } from "vue";

import type { MapReviewPopulationSurveillanceStats } from "@/type/api";

import StatCard from "../charts/StatCard.vue";
import { OKABE_ITO, formatAmount } from "../charts/theme";

const props = defineProps<{
	stats: MapReviewPopulationSurveillanceStats;
	/** The last disease week in scope; the readings are taken there. */
	lastWeek: number;
}>();

const GOOD = OKABE_ITO[2];
const BAD = OKABE_ITO[1];

/** The ordinal suffix for a percentile: 0th, 21st, 22nd, 23rd, 24th … */
function ordinal(value: number): string {
	const rounded = Math.round(value);
	const teens = rounded % 100;
	if (teens >= 11 && teens <= 13) return `${rounded}th`;
	switch (rounded % 10) {
		case 1:
			return `${rounded}st`;
		case 2:
			return `${rounded}nd`;
		case 3:
			return `${rounded}rd`;
		default:
			return `${rounded}th`;
	}
}

/** A measured value with its sign, or a dash when the server had no reading. */
function signed(value: number | null, digits: number): string {
	if (value === null) return "—";
	return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
}

const readings = computed(() => {
	const stats = props.stats;
	const slopeAccent =
		stats.slope === null
			? undefined
			: stats.slope < -2
				? GOOD
				: stats.slope > 2
					? BAD
					: undefined;
	const percentileAccent =
		stats.percentile === null
			? undefined
			: stats.percentile <= 20
				? GOOD
				: stats.percentile >= 80
					? BAD
					: undefined;
	return [
		{
			label: `Catch per trap, week ${props.lastWeek}`,
			value: stats.latest === null ? "—" : formatAmount(stats.latest),
			sub: "smoothed, per analysed sample",
			accent: undefined,
		},
		{
			label: "Against the prior five seasons",
			value: stats.percentile === null ? "—" : ordinal(stats.percentile),
			sub: "same week ± 1",
			accent: percentileAccent,
		},
		{
			label: "Change, last four weeks",
			value: stats.change4 === null ? "—" : `${signed(stats.change4, 0)}%`,
			sub: "smoothed",
			accent:
				stats.change4 === null ? undefined : stats.change4 <= 0 ? GOOD : BAD,
		},
		{
			label: "Slope, per week",
			value: stats.slope === null ? "—" : `${signed(stats.slope, 1)}%`,
			sub: "trailing six weeks, log-linear",
			accent: slopeAccent,
		},
		{
			label: "Prior five seasons' average slope",
			value:
				stats.priorAvgSlope === null
					? "—"
					: `${signed(stats.priorAvgSlope, 1)}%`,
			sub: "same week, same method",
			accent: undefined,
		},
	];
});
</script>

<template>
	<section class="population-header">
		<p class="header-title">The season at a glance</p>
		<div class="header-grid">
			<StatCard
				v-for="reading in readings"
				:key="reading.label"
				:label="reading.label"
				:value="reading.value"
				:sub="reading.sub"
				:accent="reading.accent"
			/>
		</div>
	</section>
</template>

<style scoped>
.population-header {
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
	min-width: 0;
	/* The pane's slot is a cell grid; this panel reads as one column of the
	   rail, in the order the question asks. */
	grid-column: 1 / -1;
}

.header-title {
	margin: 0 0 6px;
	font-size: 0.72rem;
	font-weight: 600;
	color: #495057;
}

.header-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 6px;
}

/* The fifth reading has the row to itself: a lone card stretched across the
   grid would read as more important than the four verdicts above it. */
.header-grid > :last-child {
	grid-column: span 2;
}
</style>
